import { Menu, MenuProps, Spin } from 'antd';
import React, { CSSProperties, Key, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { intersection } from '@/utils';
import { MenuInfo, SelectInfo } from './type';
import classnames from 'classnames';
import styles from './index.module.scss';
import { useDispatch } from 'react-redux';
import { actions as mainActions } from '@/modules/main';

export const pathnameToArray = (value = '') =>
  value === '/'
    ? ['/']
    : value
        .split('/')
        .reduce(
          (pre, current) =>
            current ? (pre.length > 0 ? [...pre, `${pre[pre.length - 1]}/${current}`] : [`/${current}`]) : [],
          [] as string[],
        );

export type RefInternalMenu = (
  props: React.PropsWithChildren<Props> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.ReactElement;

export interface Props extends Omit<MenuProps, 'selectedKeys' | 'onSelect' | 'openKeys' | 'onOpenChange' | 'onChange'> {
  value: string;
  onChange?: (value: string, info: SelectInfo | MenuInfo) => void;
  accordion?: boolean;
  loading?: boolean;
  rootClassName?: string;
  rootStyle?: CSSProperties;
}

function Index(props: Props) {
  const {
    value,
    onChange,
    items = [],
    accordion = false,
    loading = false,
    rootClassName = '',
    rootStyle,
    mode,
    ...restProps
  } = props;
  const dispatch = useDispatch();

  const [openKey, setOpenKey] = useState<React.Key[]>([]);

  const selectedKeys = useMemo(() => pathnameToArray(value), [value]);

  const levelOneKeys = useMemo(() => items.map((_) => _?.key).filter((_) => _) as Key[], [items]);

  useEffect(() => {
    if (value) {
      if (openKey.length <= 0) {
        setOpenKey(pathnameToArray(value));
      } else {
        setOpenKey([...new Set(openKey.concat(pathnameToArray(value)))]);
      }
    }
    // Adding `openKey` will result in a dead loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mode, setOpenKey]);

  // 被选中时调用
  const onSelect = useCallback(
    (selectInfo: SelectInfo) => {
      if (accordion) {
        // 只展开当前菜单的父级菜单
        setOpenKey(selectInfo.keyPath);
      }
      onChange?.(selectInfo.key, selectInfo);
      dispatch(mainActions.pushTabItem(selectInfo.key));
    },
    [dispatch, accordion, onChange],
  );

  // 处理点击已选中菜单的处理逻辑
  const onClick = (info: MenuInfo) => {
    if (selectedKeys.includes(info.key)) {
      onChange?.(info.key, info);
      dispatch(mainActions.pushTabItem(info.key));
    }
  };

  const onOpenChange = useCallback(
    (openKeys: string[]) => {
      if (accordion) {
        if (openKeys.length > 0) {
          // lastKey 为当前打开菜单
          const lastKey = openKeys.pop()!;
          const nextKeys = openKeys.filter((_) => lastKey.includes(_)).concat(lastKey);
          if (intersection(levelOneKeys, nextKeys).length > 0) {
            setOpenKey(nextKeys);
            return;
          }
        }
        setOpenKey([]);
      } else {
        setOpenKey(openKeys);
      }
    },
    [accordion, setOpenKey, levelOneKeys],
  );

  return (
    <div className={classnames(styles.compRouteMenu, rootClassName)} style={rootStyle}>
      <Spin spinning={loading}>
        <Menu
          selectedKeys={selectedKeys}
          openKeys={openKey as string[]}
          onSelect={onSelect}
          onClick={onClick}
          onOpenChange={onOpenChange}
          items={items}
          mode={mode}
          {...restProps}
        />
      </Spin>
    </div>
  );
}

export default memo(Index);
